import React, { useState, useEffect, useCallback } from 'react';
import { LifeBuoy, Loader2, ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import api from '../services/api';

// Portal problem reports — its OWN dashboard element (not the Approvals card;
// approvals are decisions, tickets are conversations with a lifecycle). The
// point of the feature is the auto-attached diagnostics, so each row expands to
// show them. Badge counts unresolved.

const STATUS = {
  open: { label: 'Open', cls: 'bg-amber-500/20 text-amber-300 border-amber-500/40' },
  in_progress: { label: 'In progress', cls: 'bg-sky-500/20 text-sky-300 border-sky-500/40' },
  resolved: { label: 'Resolved', cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' },
};

const SupportTicketsWidget = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/support-tickets');
      setItems(res.data?.tickets || []);
    } catch (e) { /* best-effort */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (t, status) => {
    setBusy(`${t.id}:${status}`);
    try {
      await api.patch(`/support-tickets/${t.id}`, { status });
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Update failed'); }
    finally { setBusy(null); }
  };

  const unresolved = items.filter((t) => t.status !== 'resolved').length;

  return (
    <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl p-6 border border-virgo-teal-700/30">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-cyan-500/20 rounded-lg">
          <LifeBuoy className="w-5 h-5 text-cyan-400" />
        </div>
        <h3 className="text-lg font-semibold text-white">Portal Problem Reports</h3>
        {unresolved > 0 && (
          <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-300 rounded-full text-sm font-medium">
            {unresolved}
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="animate-spin text-virgo-teal-400" size={24} />
        </div>
      ) : items.length === 0 ? (
        <p className="text-center text-virgo-navy-200 py-6 text-sm">No open problem reports.</p>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {items.map((t) => {
            const st = STATUS[t.status] || STATUS.open;
            const diag = t.diagnostics || {};
            const isOpen = expanded === t.id;
            return (
              <div key={t.id} className="rounded-lg border border-virgo-teal-700/20 p-3">
                <div className="flex items-start gap-2">
                  <button onClick={() => setExpanded(isOpen ? null : t.id)}
                    className="text-virgo-navy-300 hover:text-cyan-300 mt-0.5">
                    {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-2 py-0.5 rounded-full text-xs border ${st.cls}`}>{st.label}</span>
                      {t.escalated && (
                        <span className="px-2 py-0.5 rounded-full text-xs border bg-red-500/20 text-red-300 border-red-500/40 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> Escalated
                        </span>
                      )}
                      {/* contact stored + shown, de-emphasized */}
                      <span className="text-xs text-virgo-navy-300">
                        {t.contact_name || 'Client'}{t.created_at ? ` · ${new Date(t.created_at).toLocaleString()}` : ''}
                      </span>
                    </div>
                    <p className="text-sm text-white mt-1 break-words">{t.body || '(no description)'}</p>

                    {isOpen && (
                      <div className="mt-2 text-xs text-virgo-navy-100 bg-virgo-navy-900/60 rounded p-2 overflow-x-auto">
                        <div><span className="text-virgo-navy-300">Route:</span> {diag.route || '—'}</div>
                        <div className="break-all"><span className="text-virgo-navy-300">Device:</span> {diag.user_agent || '—'}</div>
                        {Array.isArray(diag.recent_api_calls) && diag.recent_api_calls.length > 0 && (
                          <div className="mt-1">
                            <span className="text-virgo-navy-300">Recent API calls:</span>
                            <ul className="ml-2">
                              {diag.recent_api_calls.slice(-6).map((c, i) => (
                                <li key={i} className="font-mono">{c.status} {c.method} {c.url}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {Array.isArray(diag.recent_errors) && diag.recent_errors.length > 0 && (
                          <div className="mt-1">
                            <span className="text-red-300">Client errors:</span>
                            <ul className="ml-2">
                              {diag.recent_errors.slice(-4).map((e, i) => (
                                <li key={i} className="font-mono break-all">{e.message}{e.src ? ` (${e.src})` : ''}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex gap-2 mt-2">
                      {t.status !== 'in_progress' && t.status !== 'resolved' && (
                        <button onClick={() => setStatus(t, 'in_progress')} disabled={busy === `${t.id}:in_progress`}
                          className="px-2.5 py-1 text-xs rounded bg-sky-600 hover:bg-sky-500 text-white">Start</button>
                      )}
                      {t.status !== 'resolved' && (
                        <button onClick={() => setStatus(t, 'resolved')} disabled={busy === `${t.id}:resolved`}
                          className="px-2.5 py-1 text-xs rounded bg-emerald-600 hover:bg-emerald-500 text-white">Resolve</button>
                      )}
                      {t.status === 'resolved' && (
                        <button onClick={() => setStatus(t, 'open')} disabled={busy === `${t.id}:open`}
                          className="px-2.5 py-1 text-xs rounded border border-virgo-teal-700/40 text-virgo-navy-100 hover:bg-virgo-teal-700/20">Reopen</button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SupportTicketsWidget;
